import { ArrowDown } from 'lucide-react'
import { SocialLinks } from '@/components/CommunitySections'
import { useLanguage } from '@/localization'
import type { CommunityData } from '@/useCommunity'

/**
 * Branding hero: the bright plaza key art (`hub-bg.png`) full-bleed, with the
 * emblem logo (`hub-logo.png`) and the ULIUNAI / COMMUNITY HUB / tagline text
 * treatment composed on top in markup (so it stays crisp and editable).
 *
 * Social links come from the shared community data (`useCommunity`) so the
 * Discord/Steam links stay in sync with every other Uliunai site.
 */
export default function Hero({ social }: { social: CommunityData['social'] }) {
  const { t } = useLanguage()

  return (
    <header className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pb-16 pt-24">
      {/* Full-bleed key-art background. The image is masked to transparent at the
          bottom so the fixed atmospheric smoke (behind all content) bleeds up
          through the hero→page boundary instead of being capped by a solid fill. */}
      <div
        aria-hidden
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(/hub-bg.png)',
          maskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, black 55%, transparent 100%)',
        }}
      />

      <div className="relative z-10 flex w-full max-w-3xl flex-col items-center text-center">
        {/* Emblem — fades in, then bobs (shared cross-game hero-logo motion) */}
        <span className="animate-fade-up">
          <img
            src="/hub-logo.png"
            alt="Uliunai"
            className="block w-28 animate-float drop-shadow-[0_6px_24px_rgba(0,0,0,0.25)] sm:w-36 md:w-44"
          />
        </span>

        {/* ULIUNAI wordmark — dark metallic on the bright plaza */}
        <h1 className="-mt-1 animate-fade-up bg-gradient-to-b from-zinc-600 via-zinc-800 to-zinc-950 bg-clip-text font-orbitron text-6xl font-black uppercase tracking-[0.12em] text-transparent drop-shadow-[0_2px_10px_rgba(255,255,255,0.4)] sm:text-7xl md:text-8xl [animation-delay:60ms]">
          Uliunai
        </h1>

        {/* COMMUNITY HUB with red accent rules */}
        <div className="mt-3 flex animate-fade-up items-center justify-center gap-4 [animation-delay:140ms]">
          <span className="h-[2px] w-10 bg-gradient-to-r from-transparent to-uliunai-red sm:w-16" />
          <span className="font-orbitron text-sm font-bold uppercase tracking-[0.45em] text-zinc-700 sm:text-base">
            {t('Community Hub')}
          </span>
          <span className="h-[2px] w-10 bg-gradient-to-l from-transparent to-uliunai-red sm:w-16" />
        </div>

        {/* Tagline */}
        <p className="mt-5 animate-fade-up font-rajdhani text-xl font-semibold text-zinc-800 sm:text-2xl [animation-delay:220ms]">
          {t('One community.')}{' '}
          <span className="font-bold text-uliunai-red">{t('Every')}</span> {t('battlefield')}.
        </p>

        {/* Social bar — placeholder links */}
        <nav
          aria-label={t('Community social links')}
          className="mt-9 flex animate-fade-up items-center justify-center gap-3 [animation-delay:300ms]"
        >
          <SocialLinks social={social} variant="light" />
        </nav>
      </div>

      {/* Scroll cue */}
      <a
        href="#games"
        aria-label={t('Scroll to games')}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 animate-fade-in text-stone-500 transition-colors hover:text-uliunai-red [animation-delay:400ms]"
      >
        <ArrowDown size={26} className="animate-bounce" />
      </a>
    </header>
  )
}
